import React, { useEffect, useRef, useState } from "react";

// Animated count-up on scroll-in. Accepts "3.2M+", "1,250+", "21+", "8.7%"
export default function StatCounter({ value, suffix = "", duration = 1800, testId }) {
  const [display, setDisplay] = useState(parseDisplay(value, 0));
  const ref = useRef(null);
  const startedRef = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !startedRef.current) {
            startedRef.current = true;
            const meta = parseValue(value);
            const start = performance.now();
            const tick = (t) => {
              const p = Math.min(1, (t - start) / duration);
              const eased = 1 - Math.pow(1 - p, 3);
              const cur = meta.numeric * eased;
              setDisplay(formatBack(cur, meta));
              if (p < 1) requestAnimationFrame(tick);
              else setDisplay(value + suffix);
            };
            requestAnimationFrame(tick);
          }
        });
      },
      { threshold: 0.4 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [value, suffix, duration]);

  return (
    <span ref={ref} data-testid={testId}>
      {display}
    </span>
  );
}

function parseValue(v) {
  // "3.2M+" -> numeric 3.2, multiplier 1e6, suffix M+
  // "1,250+" -> numeric 1250, suffix +
  // "21+" -> 21
  // "8.7%" -> 8.7 suffix %
  const str = String(v);
  let suffix = "";
  let multiplier = 1;
  let num = str.replace(/,/g, "");
  if (/M\+?$/i.test(num)) {
    multiplier = 1_000_000;
    suffix = num.match(/M\+?$/i)[0];
    num = num.replace(/M\+?$/i, "");
  } else if (/K\+?$/i.test(num)) {
    multiplier = 1_000;
    suffix = num.match(/K\+?$/i)[0];
    num = num.replace(/K\+?$/i, "");
  } else if (/%$/.test(num)) {
    suffix = "%";
    num = num.replace(/%$/, "");
  } else if (/\+$/.test(num)) {
    suffix = "+";
    num = num.replace(/\+$/, "");
  }
  return { numeric: parseFloat(num) * multiplier, suffix, hasDecimal: /\./.test(num) || /\.\d/.test(str) };
}

function formatBack(current, meta) {
  let displayNum;
  if (meta.suffix.startsWith("M")) {
    displayNum = (current / 1_000_000).toFixed(1);
  } else if (meta.suffix.startsWith("K")) {
    displayNum = (current / 1_000).toFixed(1);
  } else if (meta.suffix === "%") {
    displayNum = current.toFixed(1);
  } else {
    displayNum = Math.round(current).toLocaleString();
  }
  return `${displayNum}${meta.suffix}`;
}

function parseDisplay(v, start = 0) {
  return String(start);
}
